import type { Functions } from 'firebase/functions';
import { httpsCallable } from 'firebase/functions';
import { z } from 'zod';

import { createBackendGatewayError } from '../../utils/backendGatewayError';
import type {
  ListRejectedReportPurgeAuditHistoryArgs,
  RejectedReportPurgeAuditHistory,
  RejectedReportPurgePolicy,
  RetentionGovernanceBackendGateway,
  UpdateRejectedReportPurgePolicyArgs,
} from './retentionGovernanceGateway';

const RETENTION_GOVERNANCE_OPERATION = 'retention-governance';
const GET_POLICY_CALLABLE_NAME = 'getRejectedReportPurgePolicy';
const UPDATE_POLICY_CALLABLE_NAME = 'updateRejectedReportPurgePolicy';
const AUDIT_HISTORY_CALLABLE_NAME = 'listRejectedReportPurgeAuditHistory';
const AuditHistoryRequestSchema = z
  .object({
    limit: z.number().int().min(1).max(100).optional(),
  })
  .strict();

const NonNegativeIntegerSchema = z.number().int().nonnegative();
const MAX_RENDERABLE_DATE_TIMESTAMP_MS = 8_640_000_000_000_000;
const RenderableDateTimestampMsSchema = NonNegativeIntegerSchema.max(
  MAX_RENDERABLE_DATE_TIMESTAMP_MS
);
const OperatorAuditReferenceSchema = z.string().regex(/^[a-f0-9]{64}$/);

const RejectedReportPurgePolicyResponseSchema = z
  .object({
    policyVersion: z.literal('rejected-report-purge/v1'),
    policyRevision: z.string().min(1),
    rejectedReportRetentionDays: z.number().int().min(1).max(3650),
    automaticPurgeEnabled: z.boolean(),
    effectiveAtMs: RenderableDateTimestampMsSchema,
  })
  .strict();
const UpdateRejectedReportPurgePolicyRequestSchema = z
  .object({
    expectedPolicyRevision: z.string().min(1).max(128),
    rejectedReportRetentionDays: z.number().int().min(1).max(3650),
  })
  .strict();

const AuditEntryBaseSchema = z.object({
  policyVersion: z.literal('rejected-report-purge/v1'),
  policyRevision: z.string().min(1),
  requestedAtMs: RenderableDateTimestampMsSchema,
  completedAtMs: RenderableDateTimestampMsSchema,
});

const OperatorAuditEntryBaseSchema = AuditEntryBaseSchema.extend({
  operatorAuditReference: OperatorAuditReferenceSchema,
  operatorAuditReferenceKeyId: z.string().min(1),
});

const DryRunAuditEntrySchema = OperatorAuditEntryBaseSchema.extend({
  mode: z.literal('aggregate-dry-run'),
  status: z.literal('completed'),
  cutoffMs: RenderableDateTimestampMsSchema,
  warningCodes: z.array(z.string()),
  cumulativeCounts: z
    .object({
      scanned: NonNegativeIntegerSchema,
      eligible: NonNegativeIntegerSchema,
      activeRetentionHoldSkipped: NonNegativeIntegerSchema,
      statusMismatchSkipped: NonNegativeIntegerSchema,
      cutoffMismatchSkipped: NonNegativeIntegerSchema,
      unsupportedPolicyVersionSkipped: NonNegativeIntegerSchema,
    })
    .strict(),
}).strict();

const ApplyAuditEntrySchema = OperatorAuditEntryBaseSchema.extend({
  mode: z.literal('destructive-apply'),
  status: z.literal('completed'),
  cutoffMs: RenderableDateTimestampMsSchema,
  approvalReference: z.string().min(1),
  cumulativeCounts: z
    .object({
      scanned: NonNegativeIntegerSchema,
      removed: NonNegativeIntegerSchema,
      activeRetentionHoldSkipped: NonNegativeIntegerSchema,
      eligibilityChangedSkipped: NonNegativeIntegerSchema,
      alreadyCompleted: NonNegativeIntegerSchema,
      failedPendingRetry: NonNegativeIntegerSchema,
    })
    .strict(),
}).strict();

const AutomaticApplyAuditEntrySchema = AuditEntryBaseSchema.extend({
  mode: z.literal('automatic-destructive-apply'),
  status: z.enum(['completed', 'superseded']),
  cutoffMs: RenderableDateTimestampMsSchema,
  executionActor: z.literal('scheduled-system'),
  authorizationReference: z
    .string()
    .regex(/^retentionWorkflowPolicies\/rejected-report-purge@[^\s]+$/),
  warningCodes: z.array(z.string()),
  cumulativeCounts: z
    .object({
      scanned: NonNegativeIntegerSchema,
      removed: NonNegativeIntegerSchema,
      activeRetentionHoldSkipped: NonNegativeIntegerSchema,
      eligibilityChangedSkipped: NonNegativeIntegerSchema,
      alreadyCompleted: NonNegativeIntegerSchema,
      failedPendingRetry: NonNegativeIntegerSchema,
    })
    .strict(),
}).strict();

const PolicyUpdateAuditEntrySchema = OperatorAuditEntryBaseSchema.extend({
  mode: z.literal('policy-update'),
  status: z.literal('completed'),
  approvalReference: z.string().min(1),
  configurationChange: z
    .object({
      previousRejectedReportRetentionDays: z.number().int().min(1).max(3650),
      rejectedReportRetentionDays: z.number().int().min(1).max(3650),
    })
    .strict(),
}).strict();

const AuditHistoryResponseSchema = z
  .object({
    workflowName: z.literal('rejected-report-purge'),
    entries: z.array(
      z.discriminatedUnion('mode', [
        DryRunAuditEntrySchema,
        ApplyAuditEntrySchema,
        AutomaticApplyAuditEntrySchema,
        PolicyUpdateAuditEntrySchema,
      ])
    ),
    truncated: z.boolean(),
    malformedRecordsSkipped: NonNegativeIntegerSchema.optional(),
  })
  .strict();

const isDisabledPrecondition = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'code' in error &&
  (error as { code?: unknown }).code === 'functions/failed-precondition';

const isPolicyRevisionConflict = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const candidate = error as {
    code?: unknown;
    details?: unknown;
    customData?: { details?: unknown };
  };
  const details = candidate.details ?? candidate.customData?.details;

  return (
    candidate.code === 'functions/aborted' &&
    typeof details === 'object' &&
    details !== null &&
    'reason' in details &&
    (details as { reason?: unknown }).reason === 'policy-revision-conflict'
  );
};

export const createFirebaseRetentionGovernanceBackendGateway = (
  functions: Functions
): RetentionGovernanceBackendGateway => ({
  async getRejectedReportPurgePolicy(): Promise<RejectedReportPurgePolicy> {
    try {
      const callable = httpsCallable<Record<string, never>, unknown>(
        functions,
        GET_POLICY_CALLABLE_NAME,
        { limitedUseAppCheckTokens: true }
      );
      const response = await callable({});

      return RejectedReportPurgePolicyResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        throw createBackendGatewayError(
          RETENTION_GOVERNANCE_OPERATION,
          'policy-response-validation',
          error
        );
      }

      throw createBackendGatewayError(RETENTION_GOVERNANCE_OPERATION, 'policy-load-failed', error);
    }
  },

  async updateRejectedReportPurgePolicy(
    args: UpdateRejectedReportPurgePolicyArgs
  ): Promise<RejectedReportPurgePolicy> {
    const request = UpdateRejectedReportPurgePolicyRequestSchema.safeParse(args);
    if (!request.success) {
      throw createBackendGatewayError(
        RETENTION_GOVERNANCE_OPERATION,
        'invalid-request',
        request.error
      );
    }

    try {
      const callable = httpsCallable<UpdateRejectedReportPurgePolicyArgs, unknown>(
        functions,
        UPDATE_POLICY_CALLABLE_NAME,
        { limitedUseAppCheckTokens: true }
      );
      const response = await callable(request.data);

      return RejectedReportPurgePolicyResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        throw createBackendGatewayError(
          RETENTION_GOVERNANCE_OPERATION,
          'policy-response-validation',
          error
        );
      }

      if (isPolicyRevisionConflict(error)) {
        throw createBackendGatewayError(
          RETENTION_GOVERNANCE_OPERATION,
          'policy-revision-conflict',
          error
        );
      }

      throw createBackendGatewayError(
        RETENTION_GOVERNANCE_OPERATION,
        'policy-update-failed',
        error
      );
    }
  },

  async listRejectedReportPurgeAuditHistory(
    args: ListRejectedReportPurgeAuditHistoryArgs = {}
  ): Promise<RejectedReportPurgeAuditHistory> {
    const request = AuditHistoryRequestSchema.safeParse(args);
    if (!request.success) {
      throw createBackendGatewayError(
        RETENTION_GOVERNANCE_OPERATION,
        'invalid-request',
        request.error
      );
    }

    try {
      const callable = httpsCallable<ListRejectedReportPurgeAuditHistoryArgs, unknown>(
        functions,
        AUDIT_HISTORY_CALLABLE_NAME,
        { limitedUseAppCheckTokens: true }
      );
      const response = await callable(request.data);

      return AuditHistoryResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        throw createBackendGatewayError(
          RETENTION_GOVERNANCE_OPERATION,
          'response-validation',
          error
        );
      }

      if (isDisabledPrecondition(error)) {
        throw createBackendGatewayError(
          RETENTION_GOVERNANCE_OPERATION,
          'audit-history-disabled',
          error
        );
      }

      throw createBackendGatewayError(
        RETENTION_GOVERNANCE_OPERATION,
        'audit-history-load-failed',
        error
      );
    }
  },
});
